import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  AlertTriangle, ArrowUpLeft, Boxes, PackageX, Receipt, ShoppingBag, TrendingUp, Truck, Wallet,
} from 'lucide-react'
import { formatMoney, formatNumber } from '@najah/core'
import { t } from '@najah/i18n'
import { Alert, Badge, Card, CardBody, CardHeader, Spinner, cn } from '@najah/ui'
import { reportsApi } from '../lib/reportsApi'
import { useAuth } from '../lib/auth'
import { LineChart } from '../components/charts/LineChart'
import { BarChart } from '../components/charts/BarChart'

interface TileProps {
  label: string
  value: string
  icon: typeof Wallet
  tone: 'primary' | 'accent' | 'success' | 'warning'
  hint?: string
}

const TONES = {
  primary: 'bg-primary-subtle text-primary-subtle-fg',
  accent: 'bg-accent-subtle text-accent-subtle-fg',
  success: 'bg-success-subtle text-success',
  warning: 'bg-warning-subtle text-warning',
} as const

function StatTile({ label, value, icon: Icon, tone, hint }: TileProps) {
  return (
    <Card className="transition-shadow hover:shadow-md">
      <CardBody className="flex items-start gap-4">
        <span className={cn('grid size-11 shrink-0 place-items-center rounded-lg', TONES[tone])}>
          <Icon className="size-5" />
        </span>
        <div className="min-w-0">
          <p className="text-sm text-fg-muted">{label}</p>
          {/* رقم عرض: أرقام بعرض طبيعي — tabular يُرخي الأرقام في الأحجام الكبيرة */}
          <p className="mt-0.5 text-2xl font-bold tracking-tight text-fg" dir="ltr">{value}</p>
          {hint && <p className="mt-1 text-xs text-fg-subtle">{hint}</p>}
        </div>
      </CardBody>
    </Card>
  )
}

function AlertRow({
  to, label, detail, icon: Icon, tone,
}: { to: string; label: string; detail: string; icon: typeof Boxes; tone: 'warning' | 'danger' | 'primary' | 'info' }) {
  const tones = {
    warning: 'bg-warning-subtle text-warning',
    danger: 'bg-danger-subtle text-danger',
    primary: 'bg-primary-subtle text-primary-subtle-fg',
    info: 'bg-info-subtle text-info',
  } as const

  return (
    <Link to={to} className="flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-surface-hover">
      <span className={cn('grid size-9 shrink-0 place-items-center rounded-lg', tones[tone])}>
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-fg">{label}</p>
        <p className="text-xs text-fg-subtle">{detail}</p>
      </div>
      <ArrowUpLeft className="size-4 shrink-0 text-fg-subtle" />
    </Link>
  )
}

export function DashboardPage() {
  const { user } = useAuth()
  const { data, isPending } = useQuery({ queryKey: ['reports', 'dashboard'], queryFn: reportsApi.dashboard })

  const today = data?.today
  const month = data?.month

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-fg">
            أهلاً، {user?.fullName.split(' ')[0]}
          </h1>
          <p className="mt-1 text-sm text-fg-muted">
            {new Intl.DateTimeFormat('ar-DZ-u-nu-latn', {
              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
            }).format(new Date())}
          </p>
        </div>
        {user?.storeName && <Badge tone="primary">{user.storeName}</Badge>}
      </div>

      {isPending ? (
        <div className="grid h-40 place-items-center"><Spinner className="size-7 text-primary" /></div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <StatTile
              label={t.dashboard.todaySales} value={formatMoney(today?.revenue ?? 0)}
              icon={Wallet} tone="primary"
              hint={`${formatNumber(today?.invoiceCount ?? 0)} فاتورة`}
            />
            <StatTile
              label="ربح اليوم" value={formatMoney(today?.grossProfit ?? 0)}
              icon={TrendingUp} tone="success"
              hint={today?.marginPercent != null ? `هامش ${today.marginPercent.toFixed(1)}%` : 'لا مبيعات بعد'}
            />
            <StatTile
              label="مبيعات الشهر" value={formatMoney(month?.revenue ?? 0)}
              icon={Receipt} tone="accent"
              hint={`${formatNumber(month?.invoiceCount ?? 0)} فاتورة`}
            />
            <StatTile
              label="قيمة المخزون" value={formatMoney(data?.valuation.costValue ?? 0)}
              icon={Boxes} tone="warning" hint="بسعر التكلفة"
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader
                title="الإيراد والربح — آخر ٧ أيام"
                description="الأقدم يميناً والأحدث يساراً"
                action={
                  <Link to="/reports" className="text-xs font-medium text-primary hover:underline">
                    كل التقارير
                  </Link>
                }
              />
              <CardBody>
                <LineChart
                  height={240}
                  labels={(data?.series ?? []).map((p) => p.label)}
                  series={[
                    { key: 'revenue', label: 'الإيراد', color: 'var(--chart-1)', values: (data?.series ?? []).map((p) => p.revenue) },
                    { key: 'profit', label: 'الربح', color: 'var(--chart-2)', values: (data?.series ?? []).map((p) => p.profit) },
                  ]}
                  emptyText="لا توجد مبيعات في آخر ٧ أيام"
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="يحتاج انتباهك" />
              <CardBody className="space-y-1">
                {data?.alerts.pendingOrders ? (
                  <AlertRow
                    to="/orders" icon={Truck} tone="info"
                    label="طلبات بانتظار المراجعة"
                    detail={`${formatNumber(data.alerts.pendingOrders)} طلباً من تطبيق الزبائن`}
                  />
                ) : null}
                <AlertRow
                  to="/inventory" icon={AlertTriangle} tone="warning"
                  label="على وشك النفاد"
                  detail={`${formatNumber(data?.alerts.lowStock ?? 0)} منتجاً بلغ حدّ التنبيه`}
                />
                <AlertRow
                  to="/inventory" icon={PackageX} tone="danger"
                  label="نفدت من المخزون"
                  detail={`${formatNumber(data?.valuation.productCount ?? 0)} منتجاً في المخزون`}
                />
                {data?.alerts.supplierDebt ? (
                  <AlertRow
                    to="/suppliers" icon={ShoppingBag} tone="primary"
                    label="مستحقات للموردين"
                    detail={formatMoney(data.alerts.supplierDebt)}
                  />
                ) : null}
              </CardBody>
            </Card>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader title="الأكثر إيراداً هذا الشهر" />
              <CardBody>
                <BarChart
                  maxBars={5}
                  data={(data?.topProducts ?? []).map((p) => ({
                    key: p.productId,
                    label: p.nameAr,
                    value: p.revenue,
                    sublabel: `${formatNumber(p.quantitySold)} قطعة`,
                  }))}
                  emptyText="لا توجد مبيعات هذا الشهر"
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="أرباح الشهر" description="الإيراد ناقص التكلفة ناقص المصاريف" />
              <CardBody className="space-y-2 text-sm">
                {data?.profitAndLoss && (
                  <>
                    <div className="flex justify-between border-b border-border pb-2">
                      <span className="text-fg-muted">الإيراد</span>
                      <span className="tabular-nums text-fg" dir="ltr">{formatMoney(data.profitAndLoss.revenue)}</span>
                    </div>
                    <div className="flex justify-between border-b border-border pb-2">
                      <span className="text-fg-muted">− تكلفة البضاعة</span>
                      <span className="tabular-nums text-fg-muted" dir="ltr">{formatMoney(data.profitAndLoss.cogs)}</span>
                    </div>
                    <div className="flex justify-between border-b border-border pb-2">
                      <span className="text-fg-muted">− المصاريف</span>
                      <span className="tabular-nums text-fg-muted" dir="ltr">{formatMoney(data.profitAndLoss.expenseTotal)}</span>
                    </div>
                    <div className="flex justify-between pt-1 text-lg font-bold">
                      <span className="text-fg">صافي الربح</span>
                      <span
                        className={cn('tabular-nums', data.profitAndLoss.netProfit >= 0 ? 'text-success' : 'text-danger')}
                        dir="ltr"
                      >
                        {formatMoney(data.profitAndLoss.netProfit)}
                      </span>
                    </div>
                  </>
                )}
              </CardBody>
            </Card>
          </div>

          <Alert tone="info" title="المرحلة ٥ — التقارير جاهزة">
            كل الأرقام أعلاه محسوبة من بيانات فعلية: المبيعات والمخزون والمشتريات والمصاريف.
            التقارير التفصيلية والتصدير في قسم «التقارير».
          </Alert>
        </>
      )}
    </div>
  )
}
